import { useState, useRef, useEffect } from 'react';
import { useVideoAnimate } from '@genfun/core/hooks/useVideoAnimate';
import { fetchUserPhotos, fetchGallery, fetchRemixData } from '@genfun/core/api/API';
import { getSubscription } from '@genfun/core/api/auth';
import { LoginPopup } from '../popups/LoginPopup';
import { PricingPopup } from '../popups/PricingPopup';
import { AccountPopup } from '../popups/AccountPopup';
import { TapGame } from '../components/TapGame';
import { SiteFooter } from '../components/SiteFooter';
import { downloadFile } from '../utils/download';
import './Video.css';

function useReveal() {
    const ioRef = useRef(null);
    useEffect(() => {
        if (!ioRef.current) {
            ioRef.current = new IntersectionObserver((entries) => {
                entries.forEach(e => {
                    if (e.isIntersecting) {
                        e.target.classList.add('revealed');
                        ioRef.current.unobserve(e.target);
                    }
                });
            }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
        }
        document.querySelectorAll('.vidPage [data-reveal]:not(.revealed)')
            .forEach(el => ioRef.current.observe(el));
    });
    useEffect(() => () => { ioRef.current?.disconnect(); ioRef.current = null; }, []);
}

function useInViewport(ref, rootMargin = '200px') {
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver(([entry]) => {
            setInView(entry.isIntersecting);
        }, { rootMargin });
        io.observe(el);
        return () => io.disconnect();
    }, [ref, rootMargin]);
    return inView;
}

function HeaderMenu({ onSignOut }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onDocDown = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDocDown);
        document.addEventListener('touchstart', onDocDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocDown);
            document.removeEventListener('touchstart', onDocDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    return (
        <div className="vidHeaderMenu" ref={wrapRef}>
            <button
                className={`vidHeaderMenuBtn${open ? ' vidHeaderMenuBtn--open' : ''}`}
                onClick={() => setOpen(o => !o)}
                aria-label="More menu"
                aria-expanded={open}
            >
                <span aria-hidden="true">⋯</span>
            </button>
            {open && (
                <div className="vidHeaderMenuDropdown" role="menu">
                    <button
                        className="vidHeaderMenuItem vidHeaderMenuItem--danger"
                        role="menuitem"
                        onClick={() => { setOpen(false); onSignOut(); }}
                    >
                        Sign out
                    </button>
                </div>
            )}
        </div>
    );
}

function AmbientBackdrop({ tiles }) {
    return (
        <>
            <div className="vidAmbient" aria-hidden="true">
                <div className="vidAmbientCollage">
                    {tiles.slice(0, 6).map((t, i) => (
                        <img key={i} className="vidAmbientTile" src={t.firstFrame}
                             alt="" loading="lazy" decoding="async" />
                    ))}
                </div>
                <div className="vidAmbientBlob" />
                <div className="vidAmbientGrain" />
            </div>
            <div className="vidVignette" aria-hidden="true" />
        </>
    );
}

const CDN = 'https://genres.nyc3.cdn.digitaloceanspaces.com/claude';

function ActionCardMedia({ firstFrame, video, playEnabled }) {
    const wrapRef = useRef(null);
    const videoRef = useRef(null);
    const inView = useInViewport(wrapRef, '120px');
    const [videoLoaded, setVideoLoaded] = useState(false);

    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        if (inView) v.play().catch(() => {});
        else v.pause();
    }, [inView, playEnabled]);

    return (
        <span ref={wrapRef} className="vidActionMedia">
            <img
                className="vidActionVideo"
                src={firstFrame}
                alt=""
                loading="lazy"
                decoding="async"
                style={{ opacity: videoLoaded ? 0 : 1 }}
            />
            {playEnabled && inView && (
                <video
                    ref={videoRef}
                    className="vidActionVideo vidActionVideo--overlay"
                    src={video}
                    muted playsInline loop
                    preload="metadata"
                    onLoadedData={() => setVideoLoaded(true)}
                />
            )}
        </span>
    );
}

const VIDEO_ACTIONS = [
    { key: 'cumshot', title: 'Cumshot', video: `${CDN}/video_small/cumshot_small.mp4`, photo: `${CDN}/photo/cumshot_photo.png`, thumbnail: `${CDN}/thumbnail/cumshot_thumbnail.jpg`, firstFrame: `${CDN}/first_frame/cumshot.jpg`, featured: true, badge: 'POPULAR' },
    { key: 'reverse_cowgirl', title: 'Cowgirl', video: `${CDN}/video_small/cow_girl_small.mp4`, photo: `${CDN}/photo/cow_girl_photo.png`, thumbnail: `${CDN}/thumbnail/cow_girl_thumbnail.jpg`, firstFrame: `${CDN}/first_frame/cow_girl.jpg`, badge: 'HOT' },
    { key: 'blowjob', title: 'Blowjob', video: `${CDN}/video_small/blow_job_small.mp4`, photo: `${CDN}/photo/blow_job_photo.png`, thumbnail: `${CDN}/thumbnail/blow_job_thumbnail.jpg`, firstFrame: `${CDN}/first_frame/blow_job.jpg`, badge: 'HOT' },
    { key: 'fingering', title: 'Fingering', video: `${CDN}/video_small/fingering_small.mp4`, photo: `${CDN}/photo/fingering_photo.png`, thumbnail: `${CDN}/thumbnail/fingering_thumbnail.jpg`, firstFrame: `${CDN}/first_frame/fingering.jpg` },
    { key: 'twerking', title: 'Twerking', video: `${CDN}/video_small/twerking_small.mp4`, photo: `${CDN}/photo/twerking_photo.png`, thumbnail: `${CDN}/thumbnail/twerking_thumbnail.jpg`, firstFrame: `${CDN}/first_frame/twerking.jpg`, badge: 'HOT' },
    { key: 'full_nelson', title: 'Full Nelson', video: `${CDN}/video_small/full_nelson_small.mp4`, photo: `${CDN}/photo/full_nelson_photo.png`, thumbnail: `${CDN}/thumbnail/full_nelson_thumbnail.jpg`, firstFrame: `${CDN}/first_frame/full_nelson.jpg` },
    { key: 'posing', title: 'Posing', video: `${CDN}/video_small/posing_small.mp4`, photo: `${CDN}/photo/posing_photo.png`, thumbnail: `${CDN}/thumbnail/posing_thumbnail.jpg`, firstFrame: `${CDN}/first_frame/posing.jpg` },
    { key: 'pov_insertion', title: 'POV Insertion', video: `${CDN}/video_small/pov_insertion_small.mp4`, photo: `${CDN}/photo/pov_insertion_photo.png`, thumbnail: `${CDN}/thumbnail/pov_insertion_thumbnail.jpg`, firstFrame: `${CDN}/first_frame/pov_insertion.jpg`, badge: 'NEW' },
    { key: 'rimming', title: 'Rimming', video: `${CDN}/video_small/rimming_small.mp4`, photo: `${CDN}/photo/rimming_photo.png`, thumbnail: `${CDN}/thumbnail/rimming_thumbnail.jpg`, firstFrame: `${CDN}/first_frame/rimming.jpg` },
    { key: 'show_feet', title: 'Show Feet', video: `${CDN}/video_small/show_feet_small.mp4`, photo: `${CDN}/thumbnail/show_feet_thumbnail.jpg`, thumbnail: `${CDN}/thumbnail/show_feet_thumbnail.jpg`, firstFrame: `${CDN}/first_frame/show_feet.jpg` },
    { key: 'titfuck', title: 'Titfuck', video: `${CDN}/video_small/titfuck_small.mp4`, photo: `${CDN}/photo/titfuck_photo.png`, thumbnail: `${CDN}/thumbnail/titfuck_thumbnail.jpg`, firstFrame: `${CDN}/first_frame/titfuck.jpg`, badge: 'NEW' },
    { key: 'fisting', title: 'Fisting', video: `${CDN}/video_small/fisting_small.mp4`, photo: `${CDN}/thumbnail/fisting_thumbnail.jpg`, thumbnail: `${CDN}/thumbnail/fisting_thumbnail.jpg`, firstFrame: `${CDN}/first_frame/fisting.jpg` },
];

const ANIME_ACTIONS = [
    { key: 'titfuck_anime', title: 'Titfuck', video: 'titfuck_anime_small.mp4' },
    { key: 'blowjob_anime', title: 'Blowjob', video: 'blowjob_anime_small.mp4' },
    { key: 'twerking_anime', title: 'Twerking', video: 'twerking_anime_small.mp4' },
    { key: 'posing_anime', title: 'Posing', video: 'posing_anime_small.mp4' },
    { key: 'full_nelson_anime', title: 'Full Nelson', video: 'full_nelson_anime_small.mp4' },
    { key: 'reverse_cowgirl_anime', title: 'Cowgirl', video: 'reverse_cowgirl_anime_small.mp4' },
];


const VID_MESSAGES = ['Creating magic', 'Rendering frames', 'Polishing details', 'Almost there'];

function ProgressBar({ durationMs, startTime }) {
    const [progress, setProgress] = useState(0);
    const [msgIdx, setMsgIdx] = useState(0);
    const startRef = useRef(startTime || Date.now());
    useEffect(() => {
        const interval = setInterval(() => {
            const elapsed = Date.now() - startRef.current;
            setProgress(Math.min(95, (elapsed / durationMs) * 100));
        }, 300);
        return () => clearInterval(interval);
    }, [durationMs]);
    useEffect(() => {
        const interval = setInterval(() => {
            setMsgIdx(i => (i + 1) % VID_MESSAGES.length);
        }, 3500);
        return () => clearInterval(interval);
    }, []);
    const secsLeft = Math.max(0, Math.round((durationMs - (Date.now() - startRef.current)) / 1000));
    return (
        <div className="vidProgressWrap">
            <div className="vidProgressBar" style={{ width: `${progress}%` }} />
            <div className="vidProgressText">{VID_MESSAGES[msgIdx]}...</div>
            <div className="vidProgressTime">~{secsLeft}s remaining</div>
        </div>
    );
}

function LoadingSection({ durationMs, action }) {
    const [startTime] = useState(() => Date.now());
    const progressRef = useRef(null);
    useEffect(() => {
        const t = setTimeout(() => {
            progressRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 400);
        return () => clearTimeout(t);
    }, []);
    const onGameDone = () => {
        progressRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    return (
        <div className="vidLoadingFrame">
            <div className="vidLoadingFrameCollage" aria-hidden="true">
                {VIDEO_ACTIONS.slice(0, 4).map((a, i) => (
                    <img key={i} className={`vidLoadingFrameCollageImg vidLoadingFrameCollageImg--${i}`} src={a.firstFrame} alt="" />
                ))}
                <div className="vidLoadingFrameCollageBlob" />
            </div>
            <div className="vidLoadingFrameInner">
                {action && (
                    <div className="vidLoadingNowPill">
                        <span className="vidLoadingNowDot" />
                        Now rendering: <strong>{action.title}</strong>
                    </div>
                )}
                <div className="loadingSection">
                    <div className="loadingBigTitle">
                        <div className="loadingBigTitleMain">While you wait</div>
                        <div className="loadingBigTitleSub">Play the tap game below</div>
                    </div>
                    <div ref={progressRef} style={{ width: '100%' }}>
                        <ProgressBar durationMs={durationMs} startTime={startTime} />
                    </div>
                    <TapGame isVideo durationMs={durationMs} onDone={onGameDone} />
                </div>
                <div className="vidLoadingTip">
                    <span aria-hidden="true">💡</span>
                    Don't close this tab — your video is rendering
                </div>
            </div>
        </div>
    );
}

function useLockBodyScroll(isOpen) {
    useEffect(() => {
        if (!isOpen) return;
        const html = document.documentElement;
        const body = document.body;
        const scrollbarWidth = window.innerWidth - html.clientWidth;
        const prev = {
            bodyOverflow: body.style.overflow,
            htmlOverflow: html.style.overflow,
            bodyPaddingRight: body.style.paddingRight,
        };
        body.style.overflow = 'hidden';
        html.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
            body.style.paddingRight = `${scrollbarWidth}px`;
        }
        return () => {
            body.style.overflow = prev.bodyOverflow;
            html.style.overflow = prev.htmlOverflow;
            body.style.paddingRight = prev.bodyPaddingRight;
        };
    }, [isOpen]);
}

const AGE_KEY = 'pornoclaw_18plus';

function AgeGate({ onConfirm }) {
    useLockBodyScroll(true);
    return (
        <div className="vidAgeGateOverlay" onClick={onConfirm}>
            <div className="vidAgeGate">
                <div className="vidAgeGateCollage" aria-hidden="true">
                    {VIDEO_ACTIONS.slice(0, 8).map((a, i) => (
                        <img key={i} src={a.firstFrame} alt="" decoding="async" />
                    ))}
                </div>
                <div className="vidAgeGateBlob" aria-hidden="true" />
                <div className="vidAgeGateInner">
                    <span className="vidAgeGatePill">✦ Adults only</span>
                    <h2 className="vidAgeGateTitle">
                        You must be <span className="vidAgeGateTitleAccent">18+</span>
                        <br />to enter
                    </h2>
                    <p className="vidAgeGateText">
                        Pornoclaw contains AI-generated adult content.
                        By entering you confirm you are of legal age.
                    </p>
                    <button className="vidAgeGateBtn">
                        Enter
                        <span className="vidAgeGateBtnArrow" aria-hidden="true">→</span>
                    </button>
                    <span className="vidAgeGateHint">Tap anywhere to continue</span>
                </div>
            </div>
        </div>
    );
}

function ActionPopup({ action, isOpen, onClose, onProceed }) {
    const [videoLoaded, setVideoLoaded] = useState(false);
    useLockBodyScroll(isOpen);

    useEffect(() => { setVideoLoaded(false); }, [action?.key]);

    if (!isOpen || !action) return null;

    const videoUrl = action.video;
    const photoUrl = action.photo;

    return (
        <div className="vidPopupOverlay" onClick={onClose}>
            <div className="vidPopup" onClick={e => e.stopPropagation()}>
                <button className="vidPopupClose" onClick={onClose}>&times;</button>

                <div className="vidPopupHeader">
                    <h2 className="vidPopupTitle">{action.title}</h2>
                    <p className="vidPopupSubtitle">Customize persona in this video</p>
                </div>

                <div className="vidPopupContent">
                    {/* Left: sample photo with add image button */}
                    <div className="vidPopupSample" onClick={onProceed}>
                        <img src={photoUrl} alt={action.title} className="vidPopupSampleImg" />
                        <button className="vidPopupAddBtn">
                            <svg className="vidPopupAddBtnIcon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                            <span>Add image</span>
                        </button>
                    </div>

                    {/* Arrow */}
                    <div className="vidPopupArrow">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                            <path d="M5 12h14m0 0l-5-5m5 5l-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>

                    {/* Right: video preview */}
                    <div className="vidPopupPreview">
                        {!videoLoaded && photoUrl && (
                            <img src={photoUrl} alt={action.title} className="vidPopupPreviewMedia" />
                        )}
                        <video
                            src={videoUrl}
                            autoPlay loop muted playsInline
                            className="vidPopupPreviewMedia"
                            style={{ display: videoLoaded ? 'block' : 'none' }}
                            onLoadedData={() => setVideoLoaded(true)}
                        />
                    </div>
                </div>

                <button className="vidPopupGenerateBtn" onClick={onProceed}>
                    <span style={{ fontSize: 14 }}>▶</span>
                    Generate video
                </button>

                <div className="vidPopupDisclaimers">
                    <div className="vidPopupDisclaimer"><span className="vidPopupDisclaimerDot" /> You cannot use photos of other people.</div>
                    <div className="vidPopupDisclaimer"><span className="vidPopupDisclaimerDot" /> You are solely responsible for the images you create.</div>
                    <div className="vidPopupDisclaimer"><span className="vidPopupDisclaimerDot" /> This website is designed for entertainment purposes only.</div>
                </div>
            </div>
        </div>
    );
}

function ActionPickerPopup({ isOpen, onClose, selectedKey, onSelect }) {
    useLockBodyScroll(isOpen);
    if (!isOpen) return null;
    const allActions = VIDEO_ACTIONS;
    return (
        <div className="vidPopupOverlay" onClick={onClose}>
            <div className="vidPopup vidPopupWide" onClick={e => e.stopPropagation()}>
                <button className="vidPopupClose" onClick={onClose}>&times;</button>
                <div className="vidPopupHeader">
                    <h2 className="vidPopupTitle">Change action</h2>
                </div>
                <div className="vidActionPickerGrid">
                    {allActions.map(a => (
                        <button
                            key={a.key}
                            className={`vidActionPickerCard${a.key === selectedKey ? ' selected' : ''}`}
                            onClick={() => { onSelect(a); onClose(); }}
                        >
                            <video
                                className="vidActionPickerVideo"
                                src={a.video}
                                autoPlay muted playsInline loop
                            />
                            <span className="vidActionPickerTitle">{a.title}</span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

function ImagePickerPopup({ isOpen, onClose, sampleUrl, photoGallery, selectedImage, setSelectedImage, onCreatePhoto }) {
    useLockBodyScroll(isOpen);
    if (!isOpen) return null;
    return (
        <div className="vidPopupOverlay" onClick={onClose}>
            <div className="vidPopup vidPopupWide" onClick={e => e.stopPropagation()}>
                <button className="vidPopupClose" onClick={onClose}>&times;</button>
                <div className="vidPopupHeader">
                    <h2 className="vidPopupTitle">Choose image</h2>
                </div>
                <div className="vidImgPickerGrid">
                    {/* First row: create new + sample */}
                    <button className="vidImgPickerCreate" onClick={onCreatePhoto}>
                        <span className="vidImgPickerCreatePlus">+</span>
                        <span className="vidImgPickerCreateText">Create new photo</span>
                    </button>
                    {sampleUrl && (
                        <button
                            className={`vidImgPickerCard${selectedImage?.id === 'sample' ? ' selected' : ''}`}
                            onClick={() => {
                                setSelectedImage({ id: 'sample', url: sampleUrl, type: 'sample', sampleIdx: 1 });
                                onClose();
                            }}
                        >
                            <img src={sampleUrl} alt="Sample" />
                            <span className="vidImgPickerBadge">Sample</span>
                        </button>
                    )}
                    {/* User photos */}
                    {photoGallery.map((photo, idx) => (
                        <button
                            key={photo.media_id || idx}
                            className={`vidImgPickerCard${selectedImage?.url === photo.url ? ' selected' : ''}`}
                            onClick={() => {
                                setSelectedImage({ type: 'user', url: photo.url, mediaId: photo.media_id });
                                onClose();
                            }}
                        >
                            <img src={photo.url} alt="" loading="lazy" />
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

function Lightbox({ item, items, onClose, onChange }) {
    useLockBodyScroll(true);
    const idx = Math.max(0, items?.findIndex(i => (i.media_id || i.url) === (item.media_id || item.url)) ?? 0);
    const total = items?.length || 0;

    const goto = (next) => {
        if (!items || items.length < 2) return;
        const ni = (next + items.length) % items.length;
        onChange?.(items[ni]);
    };

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowRight') goto(idx + 1);
            else if (e.key === 'ArrowLeft') goto(idx - 1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [idx, items]); // eslint-disable-line

    const created = item.created || item.created_at || null;
    const dateText = created ? new Date(created).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : 'Recently';

    return (
        <div className="lightboxOverlay" onClick={onClose}>
            <div className="lightboxBackdropGrain" aria-hidden="true" />
            <button className="lightboxClose" onClick={onClose} aria-label="Close">&times;</button>
            {total > 1 && (
                <button
                    className="lightboxNav lightboxNav--prev"
                    onClick={(e) => { e.stopPropagation(); goto(idx - 1); }}
                    aria-label="Previous"
                >‹</button>
            )}
            {total > 1 && (
                <button
                    className="lightboxNav lightboxNav--next"
                    onClick={(e) => { e.stopPropagation(); goto(idx + 1); }}
                    aria-label="Next"
                >›</button>
            )}
            <div className="lightboxContent" onClick={e => e.stopPropagation()}>
                <video className="lightboxMedia" src={item.url} autoPlay loop muted playsInline controls />
                <div className="lightboxFooter">
                    <div className="lightboxMeta">
                        {total > 1 && <span className="lightboxMetaPos">{idx + 1} / {total}</span>}
                        <span className="lightboxMetaDot">·</span>
                        <span>{dateText}</span>
                    </div>
                    <button type="button" className="lightboxDownload" onClick={() => downloadFile(item.url)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                        Download
                    </button>
                </div>
            </div>
        </div>
    );
}

export function Video({ token, user, setSignedIn, onNavigate, refreshUser }) {
    const [phase, setPhase] = useState('grid'); // 'grid' | 'pick'
    const [popupAction, setPopupAction] = useState(null);
    const [selectedAction, setSelectedAction] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [showImagePicker, setShowImagePicker] = useState(false);
    const [showActionPicker, setShowActionPicker] = useState(false);
    const [showLogin, setShowLogin] = useState(false);
    const [showPricing, setShowPricing] = useState(false);
    const [showAccount, setShowAccount] = useState(false);
    const [adultOk, setAdultOk] = useState(() => localStorage.getItem(AGE_KEY) === '1');

    const confirmAdult = () => {
        localStorage.setItem(AGE_KEY, '1');
        setAdultOk(true);
    };

    // Credits click: pricing popup (shows the balance). Account popup only for a
    // lapsed subscription (renew flow) — cancellation lives on the Payment rules page.
    const handleCreditsClick = async () => {
        const [sub, status] = await getSubscription(token);
        if (status === 200 && sub?.status && sub.status !== 'active') setShowAccount(true);
        else setShowPricing(true);
    };

    const [video, setVideo] = useState(null);
    const [genType, setGenType] = useState(null);
    const [generated, setGenerated] = useState(false);
    const [taskId, setTaskId] = useState(null);
    const [gallery, setGallery] = useState([]);
    const [showGallery, setShowGallery] = useState(false);
    const [lightboxItem, setLightboxItem] = useState(null);
    const [photoGallery, setPhotoGallery] = useState([]);
    const [selectedImageFull, setSelectedImageFull] = useState(null);

    const resultRef = useRef(null);

    const { handleAnimate, error } = useVideoAnimate({
        token, user, selectedImage,
        action: selectedAction?.key || null,
        callbacks: {
            setGenType, setGenerated, setVideo, setTaskId,
            setShowLoginPopup: setShowLogin,
            onScrollToResults: () => { resultRef.current?.scrollIntoView({ behavior: 'smooth' }); },
        }
    });

    // Fetch full-quality image when user selects a photo
    useEffect(() => {
        if (!selectedImage?.mediaId || !token) { setSelectedImageFull(null); return; }
        setSelectedImageFull(null);
        fetchRemixData(token, selectedImage.mediaId).then(data => {
            if (data?.url) setSelectedImageFull(data.url);
        }).catch(() => {});
    }, [selectedImage?.mediaId, token]);

    // Fetch user photo gallery
    useEffect(() => {
        if (!token) return;
        fetchGallery([], 0, false, token, false, false, true, 200).then(data => {
            if (data?.content) setPhotoGallery(data.content);
        }).catch(() => {});
    }, [token]);

    // Fetch video gallery
    useEffect(() => {
        if (!token) return;
        fetchGallery([], 0, true, token, false, false, true, 200).then(data => {
            if (data?.content) setGallery(data.content);
        }).catch(() => {});
    }, [token, generated]);

    const handleActionClick = (action) => {
        setPopupAction(action);
    };

    const handleProceed = () => {
        if (!user) { setShowLogin(true); return; }
        if (!user.credits) { setPopupAction(null); setShowPricing(true); return; }
        setSelectedAction(popupAction);
        setPopupAction(null);
        setSelectedImage(null);
        setPhase('pick');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleBack = () => {
        setPhase('grid');
        setSelectedAction(null);
        setSelectedImage(null);
    };

    const handleGenerate = () => {
        if (!user) { setShowLogin(true); return; }
        if (!selectedImage) return;
        handleAnimate();
    };

    const handleCreateNew = () => {
        setPhase('grid');
        setSelectedAction(null);
        setSelectedImage(null);
        setVideo(null);
        setGenerated(false);
        setGenType(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const sampleThumbUrl = selectedAction?.photo || null;

    const showLoading = !!genType && !generated;
    const showResult = !!video && generated;
    const [savedToast, setSavedToast] = useState(false);
    const handleSaveToast = () => {
        setSavedToast(true);
        setTimeout(() => setSavedToast(false), 1600);
    };

    useReveal();

    const signOut = () => {
        localStorage.removeItem('igenfuntoken');
        window.location.reload();
    };

    return (
        <div className="vidPage">
            {!adultOk && <AgeGate onConfirm={confirmAdult} />}
            <AmbientBackdrop tiles={VIDEO_ACTIONS} />

            {/* Header */}
            <div className="vidHeader">
                <div className="vidHeaderLeft">
                    <span className="vidLogo" style={{ cursor: 'pointer' }} onClick={() => onNavigate('video')}>Pornoclaw</span>
                    <nav className="vidNav">
                        <button className="vidNavBtn vidNavBtn--active">Video</button>
                        <button className="vidNavBtn" onClick={() => onNavigate('photo')}>Photo</button>
                    </nav>
                </div>
                <div className="vidHeaderRight">
                    {!user ? (
                        <button className="vidLoginBtn" onClick={() => setShowLogin(true)}>Log in</button>
                    ) : (
                        <>
                            <button className="vidCredits" onClick={handleCreditsClick}>{user.credits ?? 0} credits</button>
                            <button className="vidSignOutBtn vidSignOutBtn--desktopOnly" onClick={signOut}>Sign out</button>
                        </>
                    )}
                    {user && <HeaderMenu onSignOut={signOut} />}
                </div>
            </div>

            {/* Phase 1: Action grid */}
            {phase === 'grid' && (
                <div className="vidSection">
                    <section className="vidHero" data-reveal>
                        <span className="vidHeroPill">✦ AI Video Generator</span>
                        <h1 className="vidHeroTitle">
                            Make a video<br />
                            <span className="vidHeroTitleAccent">with your dream girl</span>
                        </h1>
                    </section>

                    <div className="vidGridHeader" data-reveal>
                        <div className="vidGridHeaderLeft">
                            <span className="vidEyebrow">01 — Choose action</span>
                            <h2 className="vidGridTitle">{VIDEO_ACTIONS.length} actions ready to go</h2>
                        </div>
                        <span className="vidGridHint">Hover to preview · click to use</span>
                    </div>

                    <div className="vidActionGrid">
                        {VIDEO_ACTIONS.map(a => (
                            <button
                                key={a.key}
                                className={`vidActionCard${a.featured ? ' vidActionCard--featured' : ''}`}
                                onClick={() => handleActionClick(a)}
                                data-reveal
                            >
                                <ActionCardMedia firstFrame={a.firstFrame} video={a.video} playEnabled={adultOk} />
                                {a.badge && (
                                    <span className={`vidActionBadge vidActionBadge--${a.badge}`}>{a.badge}</span>
                                )}
                                <span className="vidActionTitle">{a.title}</span>
                                <span className="vidActionCta">Try <span aria-hidden="true">→</span></span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Phase 2: Two cards — Image + Action */}
            {phase === 'pick' && selectedAction && (
                <div className="vidSection">
                    <div className="vidPickBackRow">
                        <button className="vidPickBack" onClick={handleBack}>
                            <span aria-hidden="true">←</span> Back to actions
                        </button>
                    </div>

                    <div className="vidStepRow">
                        <span className={`vidStep ${selectedImage ? 'vidStep--done' : 'vidStep--active'}`}>01 · Image</span>
                        <span className="vidStepLine" />
                        <span className="vidStep vidStep--done">02 · Action</span>
                        <span className="vidStepLine" />
                        <span className={`vidStep ${selectedImage ? 'vidStep--active' : ''}`}>03 · Generate</span>
                    </div>

                    <div className="vidPickCards">
                        {/* Left: Image picker */}
                        <button
                            className={`vidPickCard vidPickCard--image${selectedImage ? ' picked' : ''}`}
                            onClick={() => setShowImagePicker(true)}
                        >
                            {selectedImage ? (
                                <img className="vidPickCardCover" src={selectedImageFull || selectedImage.url} alt="" />
                            ) : (
                                <>
                                    <span className="vidPickCardIcon">🖼</span>
                                    <span className="vidPickCardLabel">Image</span>
                                    <span className="vidPickCardHint">Tap to choose</span>
                                </>
                            )}
                            {selectedImage && <span className="vidPickCardOverlayLabel">Image</span>}
                        </button>

                        {/* Right: Action (already selected) */}
                        <button className="vidPickCard vidPickCard--action picked" onClick={() => setShowActionPicker(true)}>
                            <video
                                className="vidPickCardBgVideo"
                                src={selectedAction.video}
                                autoPlay loop muted playsInline
                            />
                            <div className="vidPickCardOverlay" />
                            <span className="vidPickCardLabel">{selectedAction.title}</span>
                            <span className="vidPickCardHint">Tap to change</span>
                        </button>
                    </div>

                    {/* Generate */}
                    {!generated && (
                        <button
                            className="vidGenerateBtn"
                            onClick={handleGenerate}
                            disabled={!selectedImage || showLoading}
                        >
                            {showLoading ? 'Generating...' : 'Generate video'}
                        </button>
                    )}
                    {generated && (
                        <button className="vidGenerateBtn" onClick={handleCreateNew}>
                            Create new
                        </button>
                    )}

                    {!generated && !showLoading && (
                        selectedImage ? (
                            <div className="vidGenerateHint">
                                <span>1 credit</span>
                                <span className="vidGenerateHintDot">·</span>
                                <span>~2 min render</span>
                                <span className="vidGenerateHintDot">·</span>
                                <span>Cinematic 1080p</span>
                            </div>
                        ) : (
                            <div className="vidGenerateHint vidGenerateHint--missing">
                                <span className="vidGenerateHintArrow">↑</span>
                                <span>Add an image to continue</span>
                            </div>
                        )
                    )}

                    {error && <div className="vidError">{error}</div>}
                </div>
            )}

            {/* Result */}
            <div ref={resultRef} />
            {(showLoading || showResult) && (
                <div className="vidResultSection">
                    {showLoading && <LoadingSection durationMs={120000} action={selectedAction} />}
                    {!showLoading && showResult && (
                        <div className="vidResultCard">
                            <div className="vidResultBurst" aria-hidden="true" />
                            <div className="vidResultMeta">
                                <span className="vidResultBadge">
                                    <span className="vidResultBadgeDot" /> Ready
                                </span>
                                <span className="vidResultMetaText">
                                    {selectedAction?.title || 'Video'} <span className="vidResultMetaDot">·</span> just now
                                </span>
                            </div>
                            <video className="vidResultVideo" src={video} autoPlay loop muted playsInline controls />
                            <div className="vidResultActions">
                                <button
                                    type="button"
                                    className="vidResultBtn vidResultBtn--primary"
                                    onClick={() => downloadFile(video)}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                    Download
                                </button>
                                <button type="button" className="vidResultBtn" onClick={handleSaveToast}>
                                    {savedToast ? (
                                        <>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                            Saved
                                        </>
                                    ) : (
                                        <>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/></svg>
                                            Save
                                        </>
                                    )}
                                </button>
                                <button type="button" className="vidResultBtn" onClick={handleCreateNew}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
                                    Create another
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Video gallery */}
            <div className="vidGallerySection" data-reveal>
                {!showGallery ? (
                    gallery.length > 0 ? (
                        <div className="vidGalleryTeaser">
                            <div className="vidGalleryTeaserHeader">
                                <span className="vidEyebrow">Your library</span>
                                <span className="vidGalleryTeaserCount">{gallery.length} video{gallery.length === 1 ? '' : 's'}</span>
                            </div>
                            <div className="vidGalleryTeaserStrip">
                                {gallery.slice(0, 4).map((item, idx) => (
                                    <button
                                        key={item.media_id || idx}
                                        className="vidGalleryTeaserItem"
                                        onClick={() => { setLightboxItem(item); }}
                                    >
                                        <video
                                            className="vidGalleryMedia"
                                            src={item.url}
                                            muted playsInline loop
                                            poster={item.frame}
                                            onMouseEnter={e => { e.target.play().catch(() => {}); }}
                                            onMouseLeave={e => { e.target.pause(); e.target.currentTime = 0; }}
                                        />
                                    </button>
                                ))}
                                {gallery.length > 4 && (
                                    <button className="vidGalleryTeaserMore" onClick={() => setShowGallery(true)}>
                                        <span className="vidGalleryTeaserMoreNum">+{gallery.length - 4}</span>
                                        <span className="vidGalleryTeaserMoreLabel">more</span>
                                    </button>
                                )}
                                {gallery.length <= 4 && (
                                    <button className="vidGalleryTeaserMore" onClick={() => setShowGallery(true)}>
                                        <span className="vidGalleryTeaserMoreNum">↓</span>
                                        <span className="vidGalleryTeaserMoreLabel">all</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="galleryEmpty">
                            <span className="galleryEmptyIcon">✨</span>
                            <span className="galleryEmptyText">Your creations will appear here</span>
                            <span className="galleryEmptyHint">Pick an action above to get started</span>
                        </div>
                    )
                ) : (
                    <>
                        <button className="galleryToggleBtn galleryToggleBtn--open" onClick={() => setShowGallery(false)}>
                            <span className="galleryToggleIcon">🎬</span>
                            <span className="galleryToggleText">Your videos · {gallery.length}</span>
                            <span className="galleryToggleArrow">↑</span>
                        </button>
                        {gallery.length > 0 ? (
                            <div className="vidGalleryGrid">
                                {gallery.map((item, idx) => (
                                    <div
                                        key={item.media_id || idx}
                                        className="vidGalleryItem"
                                        onClick={() => setLightboxItem(item)}
                                        data-reveal
                                        style={{ '--i': idx, cursor: 'pointer' }}
                                    >
                                        <video
                                            className="vidGalleryMedia"
                                            src={item.url}
                                            muted playsInline loop
                                            poster={item.frame}
                                            onMouseEnter={e => { e.target.play().catch(() => {}); }}
                                            onMouseLeave={e => { e.target.pause(); e.target.currentTime = 0; }}
                                        />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="galleryEmpty">
                                <span className="galleryEmptyIcon">✨</span>
                                <span className="galleryEmptyText">Your creations will appear here</span>
                            </div>
                        )}
                    </>
                )}
            </div>

            <SiteFooter onNavigate={onNavigate} />

            {/* Action popup (from grid) */}
            <ActionPopup
                action={popupAction}
                isOpen={!!popupAction}
                onClose={() => setPopupAction(null)}
                onProceed={handleProceed}
            />

            {/* Action picker popup */}
            <ActionPickerPopup
                isOpen={showActionPicker}
                onClose={() => setShowActionPicker(false)}
                selectedKey={selectedAction?.key}
                onSelect={setSelectedAction}
            />

            {/* Image picker popup */}
            <ImagePickerPopup
                isOpen={showImagePicker}
                onClose={() => setShowImagePicker(false)}
                sampleUrl={sampleThumbUrl}
                photoGallery={photoGallery}
                selectedImage={selectedImage}
                setSelectedImage={setSelectedImage}
                onCreatePhoto={() => onNavigate('photo')}
            />

            <LoginPopup isOpen={showLogin} setIsOpen={setShowLogin} setSignedIn={setSignedIn} />
            <PricingPopup isOpen={showPricing} onClose={() => setShowPricing(false)} token={token} user={user} overlayClassName="vidPopupOverlay" />
            <AccountPopup
                isOpen={showAccount}
                onClose={() => { setShowAccount(false); refreshUser?.(); }}
                token={token}
                user={user}
                refreshUser={refreshUser}
            />

            {/* Lightbox */}
            {lightboxItem && (
                <Lightbox
                    item={lightboxItem}
                    items={gallery}
                    onClose={() => setLightboxItem(null)}
                    onChange={setLightboxItem}
                />
            )}

            {/* Floating "now generating" pill */}
            {showLoading && selectedAction && (
                <button
                    className="vidNowGenPill"
                    onClick={() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                >
                    <span className="vidNowGenSpinner" aria-hidden="true">
                        <span className="vidNowGenSpinnerDot" />
                        <span className="vidNowGenSpinnerDot" />
                        <span className="vidNowGenSpinnerDot" />
                    </span>
                    <span>Generating <strong>{selectedAction.title}</strong></span>
                    <span className="vidNowGenPillArrow" aria-hidden="true">↑</span>
                </button>
            )}

            {/* Saved toast */}
            {savedToast && (
                <div className="vidSavedToast">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    Saved to gallery
                </div>
            )}
        </div>
    );
}
